import { CloseOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { EpicIcon } from '../../../assets/CustomIcons/EpicIcon';
import { GoToIcon } from '../../../assets/CustomIcons/GoToIcon';
import colorTokens from '../../../constants/design_system/colors';
import { queryClient } from '../../../queryClient';
import { useCreateTask } from '../../../serverState/tasks';
import { useGetActualSpaceId } from '../../../serverState/workspace';
import { Epic } from '../../../types/Epic';
import { TaskType } from '../../../types/TaskType';
import CustomTooltip from '../Tooltip/CustomTooltip';
import BaseDropdown from './BaseDropdown';
import './ItemDropdown.scss';
import NoDragWrapper from './NoDragWrapper';

export type EpicDropdownProps = {
  epic: Epic | null;
  epics: Epic[];
  onSelect?: (epic?: Epic) => void;
  onBlur: () => void;
  disabled?: boolean;
  onOpenEpic?: (epic: Epic) => void;
  size: 'small' | 'medium' | 'large';
  hideOutline?: boolean;
  defaultOpen?: boolean;
  emptyElement?: React.ReactNode;
  showGoToIcon?: boolean;
  className?: string;
  position?: 'bottom' | 'bottomLeft' | 'bottomRight' | 'topCenter' | 'topLeft' | 'topRight';
};

const EpicDropdown = (props: EpicDropdownProps) => {
  const [isDropdownOpen, setDropdownOpen] = useState(props.defaultOpen || false);
  const [scrollKey, setScrollKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const createTaskMutation = useCreateTask();
  const spaceId = useGetActualSpaceId();
  const searchInputRef = useRef<any>(null);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      // Small delay to ensure dropdown is fully rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [isDropdownOpen]);

  const onSelect = (epic: Epic) => {
    if (props.onSelect) {
      props.onSelect(epic);
    }
    isDropdownOpen && setDropdownOpen(false);
    setSearchTerm('');
  };

  // Sort epics with the selected one at top, only recalculate when the dropdown opens
  // (not while interacting) — mirrors the label dropdown's approach.
  const sortedEpics = useMemo(() => {
    return [...props.epics].sort((a, b) => {
      const aIsSelected = props.epic?.id === a.id;
      const bIsSelected = props.epic?.id === b.id;
      if (aIsSelected && !bIsSelected) return -1;
      if (!aIsSelected && bIsSelected) return 1;
      if (a.orderIndex != null && b.orderIndex == null) return -1;
      if (a.orderIndex == null && b.orderIndex != null) return 1;
      if (a.orderIndex != null && b.orderIndex != null) return a.orderIndex - b.orderIndex;
      return a.id - b.id;
    });
  }, [isDropdownOpen, props.epics]);

  const filteredEpics = sortedEpics.filter((epic) =>
    epic.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleCreateEpic = async () => {
    if (!searchTerm.trim() || !spaceId) return;

    const epicName = searchTerm.trim();

    const createdEpic = await createTaskMutation.mutateAsync({
      workspaceId: Number(spaceId),
      title: epicName,
      type: TaskType.epic,
    });

    onSelect(createdEpic as Epic);

    await queryClient.invalidateQueries({ queryKey: ['epics', Number(spaceId)] });

    setDropdownOpen(false);
    setSearchTerm('');
  };

  return (
    <NoDragWrapper className={`h-full ${props.disabled ? 'cursor-not-allowed' : ''}`}>
      <CustomTooltip title={props.disabled ? 'Epic is inherited from the parent Item and can not be changed.' : ''}>
        <BaseDropdown
          disableTransition
          trigger={['click']}
          open={isDropdownOpen}
          disabled={props.disabled}
          placement={props.position || 'bottomLeft'}
          onOpenChange={(open) => {
            if (open) setScrollKey((k) => k + 1);
            setDropdownOpen(open);
            if (!open) {
              setSearchTerm('');
              if (props.onBlur) {
                props.onBlur();
              }
            }
          }}
          dropdownRender={() => (
            <div key={scrollKey} className="flex flex-col items-start justify-start bg-brand-white shadow-md rounded-lg px-2 max-h-[350px] overflow-y-auto">
              {/* Search input */}
              <div className="w-full mb-2 sticky top-0 z-10 bg-brand-white pt-2">
                <Input
                  ref={searchInputRef}
                  placeholder="Search for Epic..."
                  prefix={<SearchOutlined className="mr-1" />}
                  allowClear={searchTerm.length > 0}
                  className="[&_input::placeholder]:!text-neutral-900 px-2 py-1 text-neutral-900 mr-auto w-[188px]"
                  value={searchTerm}
                  onChange={handleSearch}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                />
              </div>

              {filteredEpics.map((epic) => (
                <EpicLabel key={epic.id} epic={epic} size="large" onClick={() => onSelect(epic)} />
              ))}

              {/* Show message when no results */}
              {filteredEpics.length === 0 && (
                <div className="w-[188px] justify-start text-neutral-900 text-xs font-semibold">No Results</div>
              )}

              <div className="w-full mb-1"></div>

              {/* Create new epic option when searching */}
              {searchTerm.trim() && (
                <div className="w-full sticky bottom-0 bg-brand-white pb-2">
                  <div className="w-full h-px bg-neutral-300 mb-1"></div>
                  <div
                    className="w-[180px] h-full p-1 flex items-center gap-2 cursor-pointer hover:bg-neutral-200"
                    onClick={handleCreateEpic}
                  >
                    <div className="h-4 w-4 justify-center items-center flex shrink-0 text-neutral-900 rounded">
                      <PlusOutlined className="text-xs" />
                    </div>
                    <div className="py-1 text-neutral-1000 text-sm font-normal leading-tight truncate overflow-hidden whitespace-nowrap">
                      {`Create "${searchTerm.trim()}"`}
                    </div>
                  </div>
                </div>
              )}

              {/* Divider and Remove Parent option */}
              {props.epic && props.onSelect && !searchTerm.trim() && (
                <div className="w-full sticky bottom-0 bg-brand-white pb-2">
                  <div className="w-full h-px bg-neutral-300 mb-1"></div>
                  <div
                    className="w-[180px] h-full p-1 flex items-center gap-2 cursor-pointer hover:bg-neutral-200"
                    onClick={() => {
                      if (props.onSelect) {
                        props.onSelect(undefined);
                        setDropdownOpen(false);
                        setSearchTerm('');
                      }
                    }}
                  >
                    <div className="h-4 w-4 justify-center items-center flex shrink-0 text-neutral-900 rounded">
                      <CloseOutlined className="text-xs" />
                    </div>
                    <div className="py-1 text-neutral-900 text-sm font-normal leading-tight">Remove Epic</div>
                  </div>
                </div>
              )}
            </div>
          )}
        >
          <div
            className={`h-full flex items-center ${props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            style={{ outline: isDropdownOpen && !props.hideOutline ? `2px solid ${colorTokens.green[300]}` : 'none' }}
          >
            {props.epic ? (
              props.showGoToIcon ? (
                <EpicLabelWithGoToIcon
                  epic={props.epic}
                  size={props.size}
                  onOpenEpic={() => {
                    if (props.onOpenEpic && props.epic) {
                      setDropdownOpen(false);
                      props.onOpenEpic(props.epic);
                    }
                  }}
                  className={props.className}
                />
              ) : (
                <EpicLabel epic={props.epic} size={props.size} />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {props.emptyElement && props.emptyElement}
                {!props.emptyElement && <div className="px-1 text-neutral-900 text-sm font-normal">Assign Epic</div>}
              </div>
            )}
          </div>
        </BaseDropdown>
      </CustomTooltip>
    </NoDragWrapper>
  );
};

export const EpicLabelWithGoToIcon = (props: {
  epic: Epic;
  size: 'small' | 'medium' | 'large';
  onOpenEpic?: (epic: Epic) => void;
  disabled?: boolean;
  className?: string;
}) => {
  return (
    <div className={`relative parent-task ${props.className}`}>
      <EpicLabel epic={props.epic} size={props.size} disabled={props.disabled} />
      <GoToIcon
        className="absolute right-1 top-1/2 -translate-y-1/2 text-neutral-900 bg-neutral-300 rounded p-1 parent-task__go-to-icon cursor-pointer"
        onClick={(e: React.MouseEvent<SVGSVGElement>) => {
          e.stopPropagation();
          e.preventDefault();
          if (props.onOpenEpic && props.epic) {
            props.onOpenEpic(props.epic);
          }
        }}
      />
    </div>
  );
};

export const EpicLabel = (props: {
  epic: Epic;
  size: 'small' | 'medium' | 'large';
  onClick?: () => void;
  disabled?: boolean;
}) => {
  return (
    <div
      className={`${props.size === 'large' ? 'w-[180px]' : props.size === 'medium' ? 'w-[120px]' : 'max-w-[220px]'} h-full p-1 flex items-center gap-2 ${props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        } hover:bg-neutral-200`}
      onClick={props.onClick}
    >
      <div
        className="h-4 w-4 border border-solid justify-center items-center flex shrink-0"
        style={{
          backgroundColor: colorTokens.purple[500],
          borderColor: colorTokens.purple[300],
          borderRadius: '4px',
        }}
      >
        <EpicIcon className="text-white" />
      </div>
      <div className="py-1 text-neutral-900 text-sm font-normal leading-tight hover:text-brand-grey-dark truncate overflow-hidden whitespace-nowrap">
        {props.epic.title}
      </div>
    </div>
  );
};

export default EpicDropdown;
